import React, { useState } from 'react'
import { useAlert } from 'react-alert'
import { useQuery, useLazyQuery } from '@apollo/client'
import { Redirect, useLocation } from 'react-router-dom'
import { withRouter } from 'react-router'
import get from 'lodash/get'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Container from '@material-ui/core/Container'
import TextField from '@material-ui/core/TextField'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import { useAuthorization } from '../../services/authorization'
import { LINKS, USED_ID } from '../../constants'
import { useStyles } from './styles'
import logo from '../../assets/ojilogo.png'
import { USER_LOGIN, SET_NEW_PASSWORD } from '../../network/auth/queries'
import {
  PASS_REGEX,
  PASSWORD_EQUALITY_ERROR_TEXT_2,
  PASSWORD_COMPLEXITY_ERROR_TEXT,
  INVALID_EMAIL_OR_PASSWORD_TEXT
} from '../../constants/auth'

const useSearchParams = () => {
  return new URLSearchParams(useLocation().search)
}

const AccountSetupPage = ({ history }) => {
  const params = useSearchParams()
  const alert = useAlert()
  const classes = useStyles()
  const [getAuthToken, setAuthTokens] = useAuthorization()
  const [userSession, setUserSession] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [newPasswordError, setNewPasswordError] = useState('')
  const [matchPasswordsError, setMatchPasswordsError] = useState('')
  const [isRememberMeChecked, setIsRememberMeChecked] = useState(0)

  const areUserNameAndCodeProvided = () => {
    return decodeUserName(params.get('username')) && params.get('code')
  }

  const decodeUserName = userName => {
    try {
      return decodeURIComponent(userName)
    } catch (error) {
      return null
    }
  }

  const { loading: isGettingChallengeResult } = useQuery(USER_LOGIN, {
    variables: {
      userName: decodeUserName(params.get('username')),
      password: params.get('code')
    },
    fetchPolicy: 'no-cache',
    skip: !areUserNameAndCodeProvided() || userSession,
    onError: error => {
      history.push(LINKS.LOGIN)
      alert.show(error.message, {
        type: 'error'
      })
    },
    onCompleted: data => {
      const challengeResult = get(data, `security.authenticate.challengeResult`, null)
      if (challengeResult && challengeResult.session) {
        setUserSession(challengeResult.session)
      } else {
        history.push(LINKS.LOGIN)
        alert.show(INVALID_EMAIL_OR_PASSWORD_TEXT, {
          type: 'error'
        })
      }
    }
  })

  const [setNewPasswordQuery, { loading: isSettingNewPassword }] = useLazyQuery(SET_NEW_PASSWORD, {
    onError: error => {
      history.push(LINKS.LOGIN)
      alert.show(error.message, {
        type: 'error'
      })
    },
    onCompleted: data => {
      const authenticationResult = get(data, `security.respondToNewPasswordRequiredChallenge.authenticationResult`, null)
      if (authenticationResult) {
        const { accessToken, refreshToken, userId } = authenticationResult
        setAuthTokens({
          dashboardRefreshToken: refreshToken,
          dashboardAuthToken: accessToken,
          persistMode: isRememberMeChecked
        })
        localStorage.setItem(USED_ID, userId)
        history.push(LINKS.DASHBOARD)
      }
    }
  })

  const handleCheckRememberMe = () => {
    setIsRememberMeChecked(isRememberMeChecked ? 0 : 1)
  }

  const handleNewPasswordChange = e => {
    setNewPassword(e.target.value)
    setNewPasswordError('')
  }

  const handleConfirmPasswordChange = e => {
    setConfirmPassword(e.target.value)
    setMatchPasswordsError('')
  }

  const validatePasswords = () => {
    if (newPassword && confirmPassword) {
      if (newPassword.match(PASS_REGEX)) {
        if (newPassword === confirmPassword) {
          return true
        } else {
          setMatchPasswordsError(PASSWORD_EQUALITY_ERROR_TEXT_2)
          return false
        }
      } else {
        setNewPasswordError(PASSWORD_COMPLEXITY_ERROR_TEXT)
        return false
      }
    }
  }

  const handleFormSubmit = e => {
    e.preventDefault()
    if (validatePasswords()) {
      setNewPasswordQuery({
        variables: {
          userName: params.get('username'),
          newPassword,
          session: userSession
        }
      })
    }
  }

  return getAuthToken() || !areUserNameAndCodeProvided() ? (
    <Redirect to={LINKS.DASHBOARD} />
  ) : (
    <div className={classes.root}>
      <Grid container direction='column' justifyContent='center' alignItems='center' spacing={10}>
        <Grid container item direction='row' justifyContent='flex-start' alignItems='center'>
          <Grid item xs={3}>
            <img src={logo} alt='logo' />
          </Grid>
        </Grid>
        <Grid container item direction='row' justifyContent='center' alignItems='center'>
          <Grid item xs={8}>
            <Paper className={classes.paper}>
              {isGettingChallengeResult ? (
                <div className={classes.preLoader}>
                  <CircularProgress color='inherit' />
                </div>
              ) : (
                <Container>
                  <Typography variant='h1' component='div' className={classes.formHeader}>
                    Account Setup
                  </Typography>
                  <Typography variant='body1' component='div' className={classes.setupHint}>
                    To finish setting up your account, please enter a password. Your password should contain at least 8 characters,
                    uppercase and lowercase letters.
                  </Typography>
                  <form onSubmit={handleFormSubmit} className={classes.form}>
                    <div className={classes.inputBlock}>
                      <div className={classes.inputRow}>
                        {newPassword ? (
                          <Typography variant='h6' component='label' className={classes.label} htmlFor='Password'>
                            PASSWORD
                          </Typography>
                        ) : (
                          <div className={classes.labelPlaceHolder} />
                        )}
                        <TextField
                          classes={{
                            root: classes.input
                          }}
                          fullWidth
                          id='Password'
                          placeholder='Password'
                          variant='outlined'
                          name='Password'
                          value={newPassword}
                          onChange={handleNewPasswordChange}
                          inputProps={{
                            type: 'password',
                            autoComplete: 'setup-password'
                          }}
                        />
                      </div>
                      {newPasswordError && (
                        <Typography variant='h6' component='span' className={classes.errorLabel}>
                          {newPasswordError}
                        </Typography>
                      )}
                      <div className={classes.inputRow}>
                        {confirmPassword ? (
                          <Typography variant='h6' component='label' className={classes.label} htmlFor='Confirm Password'>
                            CONFIRM PASSWORD
                          </Typography>
                        ) : (
                          <div className={classes.labelPlaceHolder} />
                        )}
                        <TextField
                          classes={{
                            root: classes.input
                          }}
                          fullWidth
                          id='Confirm Password'
                          placeholder='Confirm Password'
                          variant='outlined'
                          name='Confirm Password'
                          value={confirmPassword}
                          onChange={handleConfirmPasswordChange}
                          inputProps={{
                            type: 'password',
                            autoComplete: 'confirm-setup-password'
                          }}
                        />
                      </div>
                      {matchPasswordsError && (
                        <Typography variant='h6' component='span' className={classes.errorLabel}>
                          {matchPasswordsError}
                        </Typography>
                      )}
                    </div>
                    <div className={classes.buttonBlock}>
                      {isSettingNewPassword ? (
                        <div className={classes.loader}>
                          <CircularProgress color='inherit' />
                        </div>
                      ) : (
                        <>
                          <div className={classes.rememberMeBlock}>
                            <Checkbox color='default' checked={Boolean(isRememberMeChecked)} onChange={handleCheckRememberMe} />
                            <Typography
                              variant='body2'
                              component='span'
                              className={isRememberMeChecked ? classes.rememberMeTextFilled : classes.rememberMeText}
                            >
                              Remember me next time
                            </Typography>
                          </div>
                          <Button disabled={!newPassword || !confirmPassword} className={classes.saveButton} type='submit'>
                            GET STARTED
                          </Button>
                        </>
                      )}
                    </div>
                  </form>
                </Container>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

export default withRouter(AccountSetupPage)
